import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Runs `load` on mount and then every `intervalMs` while the tab is visible.
 *
 * - A hidden tab doesn't poll; coming back refreshes at once if the data is
 *   older than one interval, then resumes the timer.
 * - `load(true)` means "a timer did this" — the caller sends the
 *   background-poll marker so the session isn't extended (decision pollsession).
 *   The first load and `refresh()` are the user's own and pass `false`.
 * - A failed run keeps the last good data; `error` says the refresh failed.
 */
export function usePolling(load: (background: boolean) => Promise<void>, intervalMs = 60_000) {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;
  const lastRef = useRef(0);

  const run = useCallback(async (background: boolean) => {
    setRefreshing(true);
    try {
      await loadRef.current(background);
      lastRef.current = Date.now();
      setLastUpdated(new Date(lastRef.current));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      if (timer === undefined) timer = setInterval(() => { void run(true); }, intervalMs);
    };
    const stop = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      if (Date.now() - lastRef.current >= intervalMs) void run(true);
      start();
    };

    void run(false);
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [run, intervalMs]);

  const refresh = useCallback(() => run(false), [run]);
  return { lastUpdated, error, refreshing, refresh };
}
